import { usePathname } from 'expo-router';
import { Kanban, LayoutDashboard, MessageSquare, Phone, Users } from 'lucide-react-native';
import { useMemo } from 'react';
import { useWindowDimensions } from 'react-native';
import { resolveActiveNavHref, TAB_ROUTE_BY_HREF } from '../../config/navigation';
import { useTabsNavigation } from '../../contexts/TabsNavigationContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { useNavigateToTab } from '../../hooks/useNavigateToTab';
import { BottomNav, type BottomNavTab } from '../primitives/BottomNav';

const MOBILE_BREAKPOINT = 780;

type TabRoute = '/dashboard' | '/leads' | '/chat' | '/voice-agents' | '/funnel';

interface TabConfig {
  key: string;
  labelKey: string;
  href: TabRoute;
  icon: BottomNavTab['icon'];
}

const TAB_CONFIG: TabConfig[] = [
  {
    key: 'dashboard',
    labelKey: 'layout.mobileTabBar.dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
  },
  { key: 'leads', labelKey: 'layout.mobileTabBar.leads', href: '/leads', icon: Users },
  {
    key: 'chat',
    labelKey: 'layout.mobileTabBar.chat',
    href: '/chat',
    icon: MessageSquare,
  },
  {
    key: 'calls',
    labelKey: 'layout.mobileTabBar.calls',
    href: '/voice-agents',
    icon: Phone,
  },
  {
    key: 'pipeline',
    labelKey: 'layout.mobileTabBar.pipeline',
    href: '/funnel',
    icon: Kanban,
  },
];

export function MobileTabBar() {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const pathname = usePathname();
  const navigateToTab = useNavigateToTab();
  const tabsNav = useTabsNavigation();
  const activeTabRouteName = tabsNav?.activeTabRouteName;

  const activeKey = useMemo(() => {
    // Prefer the Tabs navigator's focused tab (authoritative — a state-
    // preserving tab jump doesn't always refresh the pathname). Fall back to
    // the pathname mapping when the Tabs navigation isn't captured yet.
    let match = activeTabRouteName
      ? TAB_CONFIG.find((tab) => TAB_ROUTE_BY_HREF[tab.href] === activeTabRouteName)
      : undefined;
    if (!match) {
      const owningHref = resolveActiveNavHref(pathname ?? '/');
      match = TAB_CONFIG.find((tab) => tab.href === owningHref);
    }
    return match?.key ?? 'dashboard';
  }, [activeTabRouteName, pathname]);

  const tabs = useMemo<BottomNavTab[]>(
    () =>
      TAB_CONFIG.map((tab) => ({
        key: tab.key,
        label: t(tab.labelKey),
        testID: `nav_${tab.href.slice(1).replaceAll('-', '_')}_tab`,
        icon: tab.icon,
        onPress: () => navigateToTab(tab.href),
      })),
    [navigateToTab, t],
  );

  if (width >= MOBILE_BREAKPOINT) {
    return null;
  }

  return <BottomNav tabs={tabs} activeKey={activeKey} />;
}
